import { useState } from 'react';
import { useFocusOnMount } from '../../lib/hooks/useFocusOnMount';
import { COUNTRIES, isValidCountryCode } from '../../lib/countries';
import { Button } from '../Button';
import { FormField } from '../form/FormField';
import { FormSelect } from '../form/FormSelect';
import { HeadingUnderscore, RegisterLayout } from './RegisterLayout';

/**
 * Step 4 — name capture (Figma /reg-4). Phone is intentionally omitted —
 * the backend now treats phone as optional, so we don't collect it in the
 * public registration UI.
 *
 * No "Back" affordance: once the user has verified their email via OTP,
 * sending them back to re-enter the code adds no value and risks confusing
 * an authenticated PENDING user. They simply complete the profile.
 */
type ProfileStepProps = {
  initialFirstName: string;
  initialLastName: string;
  initialCountry: string;
  onAdvance: (firstName: string, lastName: string, country: string) => void;
};

const NAME_MAX = 100;

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: c.name }));

export function ProfileStep({
  initialFirstName,
  initialLastName,
  initialCountry,
  onAdvance,
}: ProfileStepProps) {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [country, setCountry] = useState(initialCountry);
  const [firstError, setFirstError] = useState<string | null>(null);
  const [lastError, setLastError] = useState<string | null>(null);
  const [countryError, setCountryError] = useState<string | null>(null);
  // Sync — no API on this step — but the AnimatePresence exit fade still
  // gives the user a window to double-tap. `transitioning` blocks re-entry
  // until the parent re-renders into the next step.
  const [transitioning, setTransitioning] = useState(false);
  const headingRef = useFocusOnMount<HTMLHeadingElement>();

  function handleSubmit(): void {
    if (transitioning) return;
    const first = firstName.trim();
    const last = lastName.trim();
    let ok = true;
    if (!first) {
      setFirstError('First name is required.');
      ok = false;
    } else if (first.length > NAME_MAX) {
      setFirstError('Please keep first name under 100 characters.');
      ok = false;
    } else {
      setFirstError(null);
    }
    if (!last) {
      setLastError('Last name is required.');
      ok = false;
    } else if (last.length > NAME_MAX) {
      setLastError('Please keep last name under 100 characters.');
      ok = false;
    } else {
      setLastError(null);
    }
    if (!country || !isValidCountryCode(country)) {
      setCountryError('Please select your country.');
      ok = false;
    } else {
      setCountryError(null);
    }
    if (!ok) return;
    setTransitioning(true);
    onAdvance(first, last, country);
  }

  return (
    <RegisterLayout
      heading={
        <>
          register
          <HeadingUnderscore />
        </>
      }
    >
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="text-center font-['Orbitron',sans-serif] text-[clamp(16px,4.8vw,22px)] uppercase tracking-[0.1em] text-white focus:outline-none"
      >
        Please enter <br className="sm:hidden" /> your details
      </h2>

      <form
        className="flex flex-col items-stretch gap-[16px]"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
        noValidate
      >
        <FormField
          label="First name"
          visuallyHiddenLabel
          autoComplete="given-name"
          placeholder="First Name*"
          value={firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            if (firstError) setFirstError(null);
          }}
          error={firstError}
          required
          maxLength={NAME_MAX}
        />
        <FormField
          label="Last name"
          visuallyHiddenLabel
          autoComplete="family-name"
          placeholder="Last Name*"
          value={lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            if (lastError) setLastError(null);
          }}
          error={lastError}
          required
          maxLength={NAME_MAX}
        />
        <FormSelect
          label="Country"
          visuallyHiddenLabel
          autoComplete="country"
          placeholder="Country*"
          options={COUNTRY_OPTIONS}
          value={country}
          onChange={(e) => {
            setCountry(e.target.value);
            if (countryError) setCountryError(null);
          }}
          error={countryError}
          required
        />

        <div className="flex flex-col items-center gap-[16px]">
          <Button
            variant="dark"
            type="submit"
            disabled={transitioning}
            className="min-w-[200px]"
          >
            Next
          </Button>
        </div>
      </form>
    </RegisterLayout>
  );
}
